import React, { forwardRef, useRef, useImperativeHandle, useState } from "react";
import { css } from "@emotion/css";
import PageHeading, { IPageHeadingProps } from "../baseComponents/PageHeading";
import NotificationMessages, { INotificationMessagesRef } from "./NotificationMessages";

const overlayStyle = css`
  width: 100%;
  background: white;
  overflow-y: auto;
  &[data-state="closed"] {
    visibility: hidden;
    opacity: 0;
  }
  &[data-state="open"] {
    visibility: visible;
  }
`;

interface ISLPageOverlayRef extends INotificationMessagesRef {
  open: () => void;
  close: () => void;
}

export interface ISLPageOverlay {
  props: {
    label: string;
    onClose?: () => void;
    children: React.ReactNode;
  };
  ref: ISLPageOverlayRef;
}

enum OverlayState {
  closed = "closed",
  open = "open"
}

const SLPageOverlay = forwardRef((props: ISLPageOverlay["props"], ref: React.Ref<ISLPageOverlay["ref"]>) => {
  const notificationsRef = useRef<INotificationMessagesRef>(null);
  const [currentState, setCurrentState] = useState<OverlayState>(OverlayState.closed);

  useImperativeHandle(ref, () => ({
    open() {
      setCurrentState(OverlayState.open);
    },
    close() {
      setCurrentState(OverlayState.closed);
    },
    setMessage(type, messages) {
      if (notificationsRef.current) {
        notificationsRef.current.setMessage(type, messages);
      }
    },
    reset() {
      if (notificationsRef.current) {
        notificationsRef.current.reset();
      }
    }
  }));

  const onClose: IPageHeadingProps["onClose"] = () => {
    setCurrentState(OverlayState.closed);
    if (props.onClose) {
      props.onClose();
    }
  };

  return (
    <div className={overlayStyle} data-state={currentState}>
      <PageHeading onClose={onClose}>{props.label}</PageHeading>
      <NotificationMessages ref={notificationsRef} />
      {props.children}
    </div>
  );
});

SLPageOverlay.displayName = "SLPageOverlay";

export default SLPageOverlay;
